import React, { useState, useEffect } from "react";
import { ListRowsSkeleton } from './PageSkeletons'
import { requestsApi } from "../services/api";
import {
  X,
  Cpu,
  Mail,
  Sparkles,
  Wand2,
  FileText,
  DollarSign,
  Zap,
  ChevronDown,
  ChevronRight,
  AlertCircle,
} from "lucide-react";

const operationIcons = {
  "Azure Content Understanding": Cpu,
  "Email Body Analysis": Mail,
  "LLM Fallback Extraction": Sparkles,
  "AI Field Normalisation": Wand2,
  "AI Learning": Wand2,
};

const operationColors = {
  "Azure Content Understanding": {
    bg: "bg-gray-50 dark:bg-gray-800/60",
    border: "border-gray-200 dark:border-gray-700",
    icon: "text-navy-600 dark:text-navy-400",
    bar: "bg-navy-600 dark:bg-navy-400",
  },
  "Email Body Analysis": {
    bg: "bg-gray-50 dark:bg-gray-800/60",
    border: "border-gray-200 dark:border-gray-700",
    icon: "text-navy-600 dark:text-navy-400",
    bar: "bg-navy-600 dark:bg-navy-400",
  },
  "LLM Fallback Extraction": {
    bg: "bg-gray-50 dark:bg-gray-800/60",
    border: "border-gray-200 dark:border-gray-700",
    icon: "text-navy-600 dark:text-navy-400",
    bar: "bg-navy-600 dark:bg-navy-400",
  },
  "AI Field Normalisation": {
    bg: "bg-gray-50 dark:bg-gray-800/60",
    border: "border-gray-200 dark:border-gray-700",
    icon: "text-navy-600 dark:text-navy-400",
    bar: "bg-navy-600 dark:bg-navy-400",
  },
  "AI Learning": {
    bg: "bg-gray-50 dark:bg-gray-800/60",
    border: "border-gray-200 dark:border-gray-700",
    icon: "text-navy-600 dark:text-navy-400",
    bar: "bg-navy-600 dark:bg-navy-400",
  },
};

function formatTokens(n) {
  if (!n) return "0";
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

function formatCost(n) {
  if (!n) return "$0.000";
  return `$${n.toFixed(4)}`;
}

function formatDuration(s) {
  if (!s) return "0s";
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const sec = (s % 60).toFixed(0);
  return `${m}m ${sec}s`;
}

export default function CostBreakdown({ requestId, onClose, embedded = false }) {
  const [cost, setCost] = useState(null);
  const [requestMeta, setRequestMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOps, setExpandedOps] = useState({});

  useEffect(() => {
    if (!requestId) return;
    setError(null);
    setLoading(true);
    Promise.all([
      requestsApi.getCostDetails(requestId),
      requestsApi.get(requestId).catch(() => null),
    ])
      .then(([costRes, reqRes]) => {
        setCost(costRes.data);
        setRequestMeta(reqRes?.data || null);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.response?.data?.error || "Failed to load cost details");
        setLoading(false);
      });
  }, [requestId]);

  const toggleOp = (idx) => {
    setExpandedOps((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const hasLearningOperation = (cost?.operations || []).some((op) =>
    /learning/i.test(op?.name || ""),
  );

  const aiLearningFromRequest =
    requestMeta?.last_job?.result_data?.ai_learning_tokens ||
    requestMeta?.last_job?.result_data?.cost_tracker?.ai_learning_tokens ||
    null;

  const aiLearningUsage = aiLearningFromRequest
    ? {
        input:
          aiLearningFromRequest.input ??
          aiLearningFromRequest.input_tokens ??
          0,
        output:
          aiLearningFromRequest.output ??
          aiLearningFromRequest.output_tokens ??
          0,
        cost:
          aiLearningFromRequest.cost ??
          aiLearningFromRequest.estimated_cost_usd ??
          0,
      }
    : null;

  const shouldMergeLearningIntoTotals =
    !!aiLearningUsage && !hasLearningOperation;

  const mergedInputTokens =
    (cost?.token_summary?.total_input_tokens || 0) +
    (shouldMergeLearningIntoTotals ? aiLearningUsage.input || 0 : 0);
  const mergedOutputTokens =
    (cost?.token_summary?.total_output_tokens || 0) +
    (shouldMergeLearningIntoTotals ? aiLearningUsage.output || 0 : 0);
  const mergedTotalTokens = mergedInputTokens + mergedOutputTokens;
  const mergedGrandTotalUsd =
    (cost?.grand_total_usd || 0) +
    (shouldMergeLearningIntoTotals ? aiLearningUsage.cost || 0 : 0);

  const totalCost = mergedGrandTotalUsd;

  const aiLearningTriggered =
    !!aiLearningUsage &&
    ((aiLearningUsage.input || 0) > 0 ||
      (aiLearningUsage.output || 0) > 0 ||
      (aiLearningUsage.cost || 0) > 0);

  const displayOperations = [...(cost?.operations || [])];
  if (aiLearningTriggered && !hasLearningOperation) {
    displayOperations.push({
      name: "AI Learning",
      description: "Learning call from user corrections",
      service: "Azure OpenAI GPT-5.4",
      details: {
        input_tokens: aiLearningUsage?.input || 0,
        output_tokens: aiLearningUsage?.output || 0,
      },
      cost_usd: aiLearningUsage?.cost || 0,
    });
  }

  useEffect(() => {
    if (!requestId || loading) return;
    if (aiLearningTriggered) {
      console.log("[AI_LEARNING] triggered", {
        requestId,
        inputTokens: aiLearningUsage?.input || 0,
        outputTokens: aiLearningUsage?.output || 0,
        costUsd: aiLearningUsage?.cost || 0,
      });
      return;
    }
    console.log("[AI_LEARNING] skipped", {
      requestId,
      reason: "No AI learning tokens/cost found",
    });
  }, [requestId, loading, aiLearningTriggered, aiLearningUsage]);

  const content = (
    <>
        {/* Header */}
        {!embedded && (
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-navy-600 to-navy-700 rounded-t-xl">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center">
              <Zap className="h-4 w-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">
                AI Processing Summary
              </h3>
              <p className="text-[10px] text-white/70">
                All Azure AI operations & costs
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="h-4 w-4 text-white/70" />
          </button>
        </div>
        )}

        {/* Content */}
        <div className={embedded ? undefined : "overflow-y-auto flex-1 px-5 py-4"}>
          {loading ? (
            <ListRowsSkeleton rows={4} />
          ) : error || !cost?.available ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2 text-gray-400">
              <AlertCircle className="h-8 w-8" />
              <span className="text-sm">
                {error || cost?.message || "No processing data available"}
              </span>
              <span className="text-xs">
                Cost data is recorded after AI processing completes.
              </span>
            </div>
          ) : (
            <>
              {/* Top stats row */}
              <div className="grid grid-cols-4 gap-2.5 mb-4">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-2.5 text-center">
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Documents
                  </p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {cost.total_documents}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-2.5 text-center">
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Pages
                  </p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {cost.total_pages}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-2.5 text-center">
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Tokens
                  </p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {formatTokens(mergedTotalTokens)}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-2.5 text-center">
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Time
                  </p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {formatDuration(cost.processing_time_seconds)}
                  </p>
                </div>
              </div>

              {/* Operations list */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1">
                  AI Operations Performed
                </p>
                {displayOperations.map((op, idx) => {
                  const Icon = operationIcons[op.name] || Cpu;
                  const colors =
                    operationColors[op.name] ||
                    operationColors["Azure Content Understanding"];
                  const isExpanded = expandedOps[idx];
                  const costPct =
                    totalCost > 0 ? (op.cost_usd / totalCost) * 100 : 0;

                  return (
                    <div
                      key={idx}
                      className={`rounded-lg border ${colors.border} ${colors.bg} overflow-hidden`}
                    >
                      {/* Operation header */}
                      <button
                        onClick={() => toggleOp(idx)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                      >
                        <Icon
                          className={`h-4 w-4 flex-shrink-0 ${colors.icon}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                              {op.name}
                            </span>
                            <span className="text-xs font-bold text-gray-900 dark:text-white ml-2">
                              {formatCost(op.cost_usd)}
                            </span>
                          </div>
                          {/* Cost bar */}
                          <div className="mt-1 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${colors.bar}`}
                              style={{ width: `${Math.max(costPct, 2)}%` }}
                            />
                          </div>
                        </div>
                        {isExpanded ? (
                          <ChevronDown className="h-3 w-3 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="h-3 w-3 text-gray-400 flex-shrink-0" />
                        )}
                      </button>

                      {/* Expanded details */}
                      {isExpanded && (
                        <div className="px-3 pb-2.5 border-t border-gray-200/50 dark:border-gray-700/50">
                          <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-2 mb-1.5 italic">
                            {op.description}
                          </p>
                          <div className="text-[11px] space-y-0.5">
                            {op.details?.pages_processed > 0 && (
                              <>
                                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                                  <span>Pages processed</span>
                                  <span className="font-medium">
                                    {op.details.cu_standard_pages ||
                                      op.details.pages_processed}{" "}
                                    std pages
                                  </span>
                                </div>
                                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                                  <span>Page fees ($0.01/page)</span>
                                  <span className="font-medium">
                                    {formatCost(
                                      op.details.page_cost ??
                                        (op.details.cu_standard_pages ||
                                          op.details.pages_processed) * 0.01,
                                    )}
                                  </span>
                                </div>
                                {op.details?.cu_tokens &&
                                  Object.keys(op.details.cu_tokens).length >
                                    0 && (
                                    <>
                                      <div className="mt-1 mb-0.5 text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">
                                        Model Token Usage (from Azure API)
                                      </div>
                                      {Object.entries(op.details.cu_tokens).map(
                                        ([model, count]) => (
                                          <div
                                            key={model}
                                            className="flex justify-between text-gray-600 dark:text-gray-400"
                                          >
                                            <span>{model}</span>
                                            <span className="font-medium">
                                              {formatTokens(count)}
                                            </span>
                                          </div>
                                        ),
                                      )}
                                      {op.details?.cu_contextualization_tokens >
                                        0 && (
                                        <div className="flex justify-between text-gray-600 dark:text-gray-400">
                                          <span>Contextualization tokens</span>
                                          <span className="font-medium">
                                            {formatTokens(
                                              op.details
                                                .cu_contextualization_tokens,
                                            )}
                                          </span>
                                        </div>
                                      )}
                                      <div className="flex justify-between text-gray-600 dark:text-gray-400 font-medium mt-0.5">
                                        <span>
                                          Token cost (
                                          {formatTokens(
                                            op.details.input_tokens,
                                          )}{" "}
                                          in +{" "}
                                          {formatTokens(
                                            op.details.output_tokens,
                                          )}{" "}
                                          out)
                                        </span>
                                        <span>
                                          {formatCost(op.details.token_cost)}
                                        </span>
                                      </div>
                                    </>
                                  )}
                              </>
                            )}
                            {op.details?.input_tokens > 0 &&
                              !op.details?.cu_tokens && (
                                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                                  <span>Input tokens</span>
                                  <span className="font-medium">
                                    {formatTokens(op.details.input_tokens)} (
                                    {formatCost(
                                      op.details.input_tokens * 0.0000025,
                                    )}
                                    )
                                  </span>
                                </div>
                              )}
                            {op.details?.output_tokens > 0 &&
                              !op.details?.cu_tokens && (
                                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                                  <span>Output tokens</span>
                                  <span className="font-medium">
                                    {formatTokens(op.details.output_tokens)} (
                                    {formatCost(
                                      op.details.output_tokens * 0.000015,
                                    )}
                                    )
                                  </span>
                                </div>
                              )}
                            {op.details?.pending_fields > 0 && (
                              <div className="flex justify-between text-gray-600 dark:text-gray-400">
                                <span>Fields filled</span>
                                <span className="font-medium">
                                  {op.details.pending_fields}
                                </span>
                              </div>
                            )}
                            {op.details?.fields_sent > 0 && (
                              <div className="flex justify-between text-gray-600 dark:text-gray-400">
                                <span>Fields normalised</span>
                                <span className="font-medium">
                                  {op.details.fields_sent}
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between text-gray-600 dark:text-gray-400">
                              <span>Service</span>
                              <span className="font-medium">{op.service}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Per-document breakdown */}
              {cost.per_document?.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                    Per-Document Breakdown
                  </p>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
                    {cost.per_document.map((doc, idx) => (
                      <div
                        key={idx}
                        className="px-3 py-2 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {doc.is_email_doc ? (
                            <Mail className="h-3.5 w-3.5 text-purple-500 flex-shrink-0" />
                          ) : (
                            <FileText className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">
                              {doc.filename}
                            </p>
                            <p className="text-[10px] text-gray-500 dark:text-gray-400">
                              {doc.is_email_doc
                                ? "Email body → OpenAI"
                                : `${doc.cu_standard_pages || doc.pages} std pages → Azure CU`}
                              {doc.processing_time_seconds
                                ? ` • ${doc.processing_time_seconds}s`
                                : ""}
                            </p>
                          </div>
                        </div>
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300 ml-2">
                          {formatCost(doc.azure_cu_cost || 0)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Token summary */}
              {cost.token_summary && (
                <div className="mt-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                  <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Token Usage Summary
                  </p>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400">
                        Input
                      </p>
                      <p className="text-sm font-bold text-gray-900 dark:text-white">
                        {formatTokens(mergedInputTokens)}
                      </p>
                      <p className="text-[10px] text-gray-400">
                        {formatCost(mergedInputTokens * 0.0000025)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400">
                        Output
                      </p>
                      <p className="text-sm font-bold text-gray-900 dark:text-white">
                        {formatTokens(mergedOutputTokens)}
                      </p>
                      <p className="text-[10px] text-gray-400">
                        {formatCost(mergedOutputTokens * 0.000015)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400">
                        Total
                      </p>
                      <p className="text-sm font-bold text-gray-900 dark:text-white">
                        {formatTokens(mergedTotalTokens)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {cost?.available && !loading && (
          <div className={embedded ? "mt-4 px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30" : "px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 rounded-b-xl"}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-green-600 dark:text-green-400" />
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  Total AI Cost
                </span>
              </div>
              <span className="text-lg font-bold text-green-700 dark:text-green-300">
                {formatCost(mergedGrandTotalUsd)}
              </span>
            </div>
            {cost.total_pages > 0 && (
              <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                Avg: {formatCost(mergedGrandTotalUsd / cost.total_pages)}/page •{" "}
                {formatCost(
                  mergedGrandTotalUsd / Math.max(cost.total_documents, 1),
                )}
                /document
              </p>
            )}
          </div>
        )}
    </>
  );

  if (embedded) {
    return <div className="flex flex-col">{content}</div>;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-navy-900/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-lg w-full mx-4 max-h-[85vh] flex flex-col">
        {content}
      </div>
    </div>
  );
}
